// Source: trinity/trinity/Shader/Tr2EffectDescription.h
import { CjsSchema, impl, type } from "@carbonenginejs/runtime-utils/schema";
import { CjsModel } from "@carbonenginejs/runtime-utils/model";
import {
  isPlainObject
} from "@carbonenginejs/runtime-utils/is";
import { copyBytes } from "@carbonenginejs/runtime-utils/bytes";
import { Tr2EffectStageInput } from "./Tr2EffectStageInput.js";
import {
  recordBytes,
  recordText,
  toRecordBlob,
  toRecordText
} from "./carbonRecordFields.js";

/** Reflected shader-library metadata. */
export class Tr2EffectLibrary extends CjsModel
{

  /** payloadSize (uint32_t) */
  payloadSize = 0;

  /** libraryHandle (uint32_t) */
  libraryHandle = 0;

  /** rayGenName (BlueSharedStringW) */
  rayGenName = "";

  /** missName (BlueSharedStringW) */
  missName = "";

  /** closestHitName (BlueSharedStringW) */
  closestHitName = "";

  /** anyHitName (BlueSharedStringW) */
  anyHitName = "";

  /** intersectionName (BlueSharedStringW) */
  intersectionName = "";

  /** hitGroupName (BlueSharedStringW) */
  hitGroupName = "";

  /** globalInput (Tr2EffectStageInput) */
  globalInput = null;

  /** localInput (Tr2EffectStageInput) */
  localInput = null;

  /** globalResourceSetDesc (Tr2ResourceSetDescriptionAL) */
  globalResourceSetDesc = null;

  /** Exact source library program metadata and owned bytes. */
  sourceProgram = null;

  /** Portable export records retained before backend library registration. */
  exports = [];

  /**
   * Construct one canonical library from JS/JSON model values.
   *
   * @param {object} values Canonical model values.
   * @param {object} options CjsModel import options.
   * @returns {Tr2EffectLibrary} Hydrated library.
   */
  static from(values = {}, options = {})
  {
    let normalized = values;
    const inputs = new Map();
    for (const field of [ "globalInput", "localInput" ])
    {
      if (values?.[field]
        && !(values[field] instanceof Tr2EffectStageInput))
      {
        normalized = normalized === values ? { ...values } : normalized;
        normalized[field] = Tr2EffectStageInput.from(
          values[field],
          options
        );
        inputs.set(field, normalized[field]);
      }
    }
    const library = super.from(normalized, options);
    for (const [ field, value ] of inputs)
    {
      library[field] = value;
    }
    return library;
  }

  /**
   * Build one raytracing library from its Carbon v15 description record.
   *
   * A library carries two `StageData` blocks of its own — a global and a local
   * input — which are the same block a stage carries, minus the stage wrapper.
   *
   * @param {object} record Carbon library record.
   * @returns {Tr2EffectLibrary} Reflected library.
   */
  static fromCarbonBinary(record)
  {
    if (!isPlainObject(record))
    {
      throw new TypeError("Carbon effect library record must be an object");
    }

    const library = new this();
    library.payloadSize = record.payloadSize;
    library.hitGroupName = recordText(record.hitGroupName);
    library.exports = record.exports.map(entry => ({
      type: entry.type,
      name: recordText(entry.name)
    }));
    // The export type is the only thing that names an entry point; the record
    // stores a flat list and the named fields are a view onto it.
    for (const entry of library.exports)
    {
      if (entry.type === 0) library.rayGenName = entry.name;
      if (entry.type === 1) library.missName = entry.name;
      if (entry.type === 2) library.closestHitName = entry.name;
      if (entry.type === 3) library.anyHitName = entry.name;
      if (entry.type === 4) library.intersectionName = entry.name;
    }
    library.sourceProgram = {
      kind: "library",
      bytes: copyBytes(recordBytes(record.shaderData)),
      shaderSize: record.shaderData.size,
      // See Tr2EffectStageInput: only a zero-size blob's word is portable.
      unsetOffset: record.shaderData.size === 0
        ? record.shaderData.offset
        : 0xffffffff
    };
    library.globalInput = Tr2EffectStageInput.fromCarbonBinaryInput(
      record.globalInputs
    );
    library.localInput = Tr2EffectStageInput.fromCarbonBinaryInput(
      record.localInputs
    );
    return library;
  }


  /**
   * Emit this library as a Carbon v15 record.
   *
   * `exports` is authoritative; the named entry-point fields are a view onto it
   * and are not written separately.
   *
   * @returns {object} Carbon library record.
   */
  toCarbonBinary()
  {
    const program = this.sourceProgram ?? {};
    return {
      payloadSize: this.payloadSize >>> 0,
      shaderData: toRecordBlob(
        program.bytes,
        program.shaderSize,
        program.unsetOffset
      ),
      exports: this.exports.map(entry => ({
        type: entry.type,
        name: toRecordText(entry.name)
      })),
      hitGroupName: toRecordText(this.hitGroupName),
      globalInputs: this.globalInput.toCarbonBinaryInput(),
      localInputs: this.localInput.toCarbonBinaryInput()
    };
  }
}

// Declared imperatively rather than with decorators, so this module stays
// plain ESM that loads from source without a transform. The decorator
// expressions are reused verbatim, so the registered metadata is identical.
// Statics belong in `methods`: decorateMethod targets the prototype and
// would register a static as an instance field.
CjsSchema.define(Tr2EffectLibrary, {
  className: "Tr2EffectLibrary",
  family: "shader",
  fields: {
    payloadSize: type.uint32,
    libraryHandle: type.uint32,
    rayGenName: type.string,
    missName: type.string,
    closestHitName: type.string,
    anyHitName: type.string,
    intersectionName: type.string,
    hitGroupName: type.string,
    globalInput: type.rawStruct("Tr2EffectStageInput"),
    localInput: type.rawStruct("Tr2EffectStageInput"),
    globalResourceSetDesc: type.rawStruct("Tr2ResourceSetDescriptionAL"),
    sourceProgram: [ impl.adapted, impl.reason("Carbon registers the library with the renderer while reading; the device-free graph retains the source program for later engine realization."), type.rawStruct("CjsEffectSourceProgram") ],
    exports: [ impl.adapted, impl.reason("Carbon resolves these exports into a renderer library handle; the device-free graph keeps the declarative export list."), type.rawStruct("CjsEffectLibraryExports") ]
  }
});
